// Amélioration qualité (upscale IA via /api/upscale-dtf).
// Le CDN WaveSpeed ne sert que du JPEG : la transparence serait perdue.
// On upscale donc séparément les couleurs aplaties et le masque alpha,
// puis on recombine sur canvas pour reconstruire un PNG RGBA.

const API_URL = '/api/upscale-dtf';

// Le modèle (recraft-crisp-upscale) plafonne sa sortie à ~4,2 Mpx (2²² px),
// quel que soit l'input : re-améliorer une image déjà proche du plafond ne
// gagne rien. Mesuré le 2026-08-15 : 724×1280 → 1540×2723, et 1540×2723 →
// 1540×2723 (aucun gain).
const MODEL_OUT_PX = 4_194_304;
// limite de payload du proxy /api/upscale-dtf (MAX_IMAGE_CHARS côté serveur)
const MAX_PAYLOAD_CHARS = 4_200_000;

export class UpscaleMaxedError extends Error {
	constructor() {
		super('résolution maximale du modèle déjà atteinte');
	}
}

// gain linéaire minimal pour qu'un appel (payant) vaille la peine
const MIN_GAIN = 1.1;

export const upscaleGain = (pxW: number, pxH: number): number => Math.sqrt(MODEL_OUT_PX / (pxW * pxH));

export const canUpscale = (pxW: number, pxH: number): boolean => upscaleGain(pxW, pxH) >= MIN_GAIN;

/** Dimensions attendues après amélioration — prédictibles car la sortie du modèle est ~fixe. */
export const predictUpscalePx = (pxW: number, pxH: number): { w: number; h: number } => {
	const s = upscaleGain(pxW, pxH);
	return { w: Math.round(pxW * s), h: Math.round(pxH * s) };
};

export interface UpscaledImage {
	src: string;
	aspect: number;
}

const loadImg = (src: string, cross = false): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const im = new Image();
		if (cross) im.crossOrigin = 'anonymous';
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('image illisible'));
		im.src = src;
	});

const mkCanvas = (w: number, h: number) => {
	const c = document.createElement('canvas');
	c.width = w;
	c.height = h;
	return c;
};

// JPEG (pas PNG) : un PNG photo dépasse vite la limite serveur (413) — c'est
// ce qui bloquait la ré-amélioration. Baisse la qualité si besoin pour tenir.
const toJpegUnderLimit = (c: HTMLCanvasElement): string => {
	for (const q of [0.92, 0.85, 0.75]) {
		const u = c.toDataURL('image/jpeg', q);
		if (u.length <= MAX_PAYLOAD_CHARS) return u;
	}
	throw new Error('image trop lourde pour le service d’amélioration');
};

const callApi = async (image: string): Promise<string> => {
	const r = await fetch(API_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ image }),
	});
	if (!r.ok) throw new Error(`upscale api ${r.status}`);
	const j = (await r.json()) as { url?: string };
	if (!j.url) throw new Error('upscale api: réponse sans url');
	return j.url;
};

// Fait « saigner » les couleurs des bords dans les zones transparentes pour
// éviter un liseré blanc après aplatissement (dilatation 4-voisins).
const bleed = (data: Uint8ClampedArray, w: number, h: number, passes: number) => {
	for (let p = 0; p < passes; p++) {
		const prev = data.slice();
		for (let y = 0; y < h; y++) {
			for (let x = 0; x < w; x++) {
				const i = (y * w + x) * 4;
				if (prev[i + 3] > 0) continue;
				let r = 0,
					g = 0,
					b = 0,
					n = 0;
				if (x > 0 && prev[i - 4 + 3] > 0) {
					r += prev[i - 4];
					g += prev[i - 3];
					b += prev[i - 2];
					n++;
				}
				if (x < w - 1 && prev[i + 4 + 3] > 0) {
					r += prev[i + 4];
					g += prev[i + 5];
					b += prev[i + 6];
					n++;
				}
				if (y > 0 && prev[i - w * 4 + 3] > 0) {
					r += prev[i - w * 4];
					g += prev[i - w * 4 + 1];
					b += prev[i - w * 4 + 2];
					n++;
				}
				if (y < h - 1 && prev[i + w * 4 + 3] > 0) {
					r += prev[i + w * 4];
					g += prev[i + w * 4 + 1];
					b += prev[i + w * 4 + 2];
					n++;
				}
				if (n) {
					data[i] = r / n;
					data[i + 1] = g / n;
					data[i + 2] = b / n;
					data[i + 3] = 255;
				}
			}
		}
	}
};

interface SplitResult {
	flatUrl: string;
	maskUrl: string | null;
}

const splitAlpha = (im: HTMLImageElement): SplitResult => {
	const w = im.naturalWidth;
	const h = im.naturalHeight;
	const base = mkCanvas(w, h);
	const bctx = base.getContext('2d')!;
	bctx.drawImage(im, 0, 0);
	const id = bctx.getImageData(0, 0, w, h);
	const d = id.data;
	let transparent = false;
	for (let i = 3; i < d.length; i += 4) {
		if (d[i] < 250) {
			transparent = true;
			break;
		}
	}
	if (!transparent) return { flatUrl: toJpegUnderLimit(base), maskUrl: null };

	// masque : alpha → niveaux de gris opaques
	const mask = mkCanvas(w, h);
	const mctx = mask.getContext('2d')!;
	const mid = mctx.createImageData(w, h);
	for (let i = 0; i < d.length; i += 4) {
		const a = d[i + 3];
		mid.data[i] = a;
		mid.data[i + 1] = a;
		mid.data[i + 2] = a;
		mid.data[i + 3] = 255;
	}
	mctx.putImageData(mid, 0, 0);

	// couleurs aplaties : bleed puis fond blanc là où rien n'a saigné
	const flatData = id.data.slice();
	bleed(flatData, w, h, w * h > 2_000_000 ? 2 : 6);
	for (let i = 0; i < flatData.length; i += 4) {
		if (flatData[i + 3] === 0) {
			flatData[i] = 255;
			flatData[i + 1] = 255;
			flatData[i + 2] = 255;
		}
		flatData[i + 3] = 255;
	}
	const flat = mkCanvas(w, h);
	const fctx = flat.getContext('2d')!;
	const fid = fctx.createImageData(w, h);
	fid.data.set(flatData);
	fctx.putImageData(fid, 0, 0);
	// masque : PNG (niveaux de gris, léger) ; repli JPEG si vraiment trop lourd
	let maskUrl = mask.toDataURL('image/png');
	if (maskUrl.length > MAX_PAYLOAD_CHARS) maskUrl = mask.toDataURL('image/jpeg', 0.92);
	return { flatUrl: toJpegUnderLimit(flat), maskUrl };
};

export async function upscaleImage(src: string): Promise<UpscaledImage> {
	const im = await loadImg(src);
	if (!im.naturalWidth || !im.naturalHeight) throw new Error('image illisible');
	const inPx = im.naturalWidth * im.naturalHeight;
	// gain attendu insuffisant → inutile : on refuse AVANT tout appel API
	if (!canUpscale(im.naturalWidth, im.naturalHeight)) throw new UpscaleMaxedError();
	const { flatUrl, maskUrl } = splitAlpha(im);

	// séquentiel (pas Promise.all) : ordre des requêtes déterministe
	const rgbOut = await loadImg(await callApi(flatUrl), true);
	const W = rgbOut.naturalWidth;
	const H = rgbOut.naturalHeight;
	// sortie ≈ entrée : plafond du modèle atteint, ne pas remplacer l'image
	// (le remplacement ferait perdre de la qualité via la recompression JPEG)
	if (W * H < inPx * 1.15) throw new UpscaleMaxedError();
	const out = mkCanvas(W, H);
	const octx = out.getContext('2d')!;
	octx.drawImage(rgbOut, 0, 0);

	if (maskUrl) {
		const maskOut = await loadImg(await callApi(maskUrl), true);
		const mc = mkCanvas(W, H);
		const mcx = mc.getContext('2d')!;
		mcx.drawImage(maskOut, 0, 0, W, H);
		const md = mcx.getImageData(0, 0, W, H).data;
		const oid = octx.getImageData(0, 0, W, H);
		for (let i = 0; i < oid.data.length; i += 4) oid.data[i + 3] = md[i];
		octx.putImageData(oid, 0, 0);
	}
	return { src: out.toDataURL('image/png'), aspect: W / H };
}
